import { useState, useCallback } from "react";
import { observer } from "mobx-react";
import DialogRebaseline from "../dialogs/DialogRebaseline";
import ProjectTreeView from "../components/ProjectTreeView";
import Task from "../model/Task";
import TaskPanel from "../components/TaskPanel";
import TaskPackage from "../model/TaskPackage";
import TaskPackagePanel from "../components/TaskPackagePanel";
import { useSnackbar } from "notistack";
import { getUnqId } from "../operations/ProjectOperations";
import IconButton from "@mui/material/IconButton";
import ListSubheader from "@mui/material/ListSubheader";
import { Menu, MenuItem } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";
import { formatISO } from "date-fns";
import { useProjectStore } from "../model/ProjectStore";
import { useViewState } from "../model/ViewState";
import CollapsiblePanel from "../components/CollapsiblePanel";

const GetViewPaneForCode = ({ selectedTask }) => {
  if (selectedTask instanceof TaskPackage) {
    return (
      <TaskPackagePanel selectedTask={selectedTask} />
    );
  } else if (selectedTask instanceof Task) {
    return (
      <TaskPanel
        selectedTask={selectedTask}
      />
    );
  }
};

const generateUniqueName = (dft, taskType) => {
  // Define regex grammar match
  let defaultTaskNamePrefix = taskType === Task ? "TASK " : "TASK SUMMARY ";
  let regex = new RegExp(defaultTaskNamePrefix + "[0-9]+");

  // Find unique name by getting default name with highest number at the end
  let tasks = dft.filter((task) => task instanceof taskType);
  let tasksWithDefaultName = tasks.filter((task) => task.name.match(regex));
  let maxNumber = tasksWithDefaultName
    .map((t) => t.name.replace(defaultTaskNamePrefix, ""))
    .sort((a, b) => parseInt(a) - parseInt(b))
    .pop();
  let numTasks = tasks.length;
  let nameCheck = (task) =>
    task.name === defaultTaskNamePrefix + (numTasks + 1).toString();

  // Find unique name by counting all tasks of specified type
  while (tasks.some(nameCheck)) {
    numTasks += 1;
  }

  // Use maxNumber for unique name if there exists a task with default generated name, otherwise use the count of total tasks of specified type.
  let uniqueNumber = isNaN(maxNumber)
    ? numTasks
    : Math.max(numTasks, parseInt(maxNumber) + 1);
  return defaultTaskNamePrefix + uniqueNumber.toString();
};

const updateDisplayIndex = (dft) => {
  let taskPackages = dft.filter((tp) => tp instanceof TaskPackage);

  // First update all ids
  for (let i = 0; i < dft.length; i++) {
    dft[i].displayIndex = i.toString();
  }

  // Then update parent id with new id allocation
  for (let i = 0; i < dft.length; i++) {
    if (i === 0) {
      dft[i].parent = "";
      dft[i].parentDisplayIndex = "";
    } else {
      if (dft[i] instanceof Task) {
        // Look through tasks array
        taskPackages.forEach((tp) => {
          if (tp.tasks.filter((task) => task.id === dft[i].id).length > 0) {
            dft[i].parent = tp.id;
            dft[i].parentDisplayIndex = tp.displayIndex;
          }
        });
      } else if (dft[i] instanceof TaskPackage) {
        // Look through task package array
        taskPackages.forEach((tp) => {
          if (
            tp.taskPackages.filter(
              (taskPackage) => taskPackage.displayIndex === dft[i].displayIndex
            ).length > 0
          ) {
            dft[i].parent = tp.id;
            dft[i].parentDisplayIndex = tp.displayIndex;
          }
        });
      }
    }
  }
};

const TaskView = () => {
  const { enqueueSnackbar } = useSnackbar();
  const project = useProjectStore();
  const view = useViewState();
  let [treeDrawerOpen, setTreeDrawerOpen] = useState(true);
  const [optionsMenu, setoptionsMenu] = useState(null);
  const [preBaselineOpen, setPreBaselineOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(240);

  useEffect(() => {
    project.checkProjUpdate(project.id, project.currentHash, enqueueSnackbar)
      .catch((error) => {
        enqueueSnackbar(error.message, { variant: "error" });
        if (error.cause === "opaqueredirect") {
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
        }
      });
  }, [enqueueSnackbar, project]);

  const handlePreBaselineOpen = () => {
    setPreBaselineOpen(true);
  };

  const handlePreBaselineClose = () => {
    setPreBaselineOpen(false);
    setoptionsMenu(null);
  };

  let selectedTask = project.rootTaskPackage
    .depthFirstTraversal()
    .find((t) => t.id === view.selectedTaskId) ?? project.rootTaskPackage;

  const handleClickOptionsMenu = (event) => {
    setoptionsMenu(event.currentTarget);
  };

  const handleAddSummaryTask = () => {
    let dft = project.rootTaskPackage.depthFirstTraversal();

    if (selectedTask instanceof TaskPackage) {
      getUnqId((generatedId) => {
        selectedTask.taskPackages.push(
          TaskPackage.BasicTaskPackage(
            selectedTask.generateUniqueCode(),
            project,
            generatedId,
            selectedTask.displayIndex + selectedTask.taskPackages.length + 1,
            generateUniqueName(dft, TaskPackage),
            selectedTask.id,
            selectedTask.displayIndex,
            formatISO(new Date())
          )
        );
        // Update DisplayIndex of all tasks to match the change in tree hierarchy
        updateDisplayIndex(project.rootTaskPackage.depthFirstTraversal());
        setoptionsMenu(null);
      });
    } else if (selectedTask instanceof Task) {
      getUnqId((generatedId) => {
        let parent =
          selectedTask.parent === "0"
            ? project.rootTaskPackage
            : dft.find((t) => t.id === selectedTask.parent);
        parent.taskPackages.push(
          TaskPackage.BasicTaskPackage(
            parent.generateUniqueCode(),
            project,
            generatedId,
            parent.displayIndex + parent.taskPackages.length + 1,
            generateUniqueName(dft, TaskPackage),
            selectedTask.parent,
            selectedTask.parentDisplayIndex,
            formatISO(new Date())
          )
        );
        // Update DisplayIndex of all tasks to match the change in tree hierarchy
        updateDisplayIndex(project.rootTaskPackage.depthFirstTraversal());
        setoptionsMenu(null);
      });
    }
  };

  const handleAddTask = async () => {
    let dft = project.rootTaskPackage.depthFirstTraversal();

    if (selectedTask instanceof TaskPackage) {
      getUnqId((generatedId) => {
        selectedTask.tasks.push(
          Task.BasicTask(
            selectedTask.generateUniqueCode(),
            project,
            generatedId,
            selectedTask.displayIndex +
            selectedTask.taskPackages.length +
            selectedTask.tasks.length +
            1,
            generateUniqueName(dft, Task),
            selectedTask.id,
            selectedTask.displayIndex,
            formatISO(new Date())
          )
        );
        // Update DisplayIndex of all tasks to match the change in tree hierarchy
        updateDisplayIndex(project.rootTaskPackage.depthFirstTraversal());
        setoptionsMenu(null);
      });
    } else if (selectedTask instanceof Task) {
      getUnqId((generatedId) => {
        let parent =
          selectedTask.parent === "0"
            ? project.rootTaskPackage
            : dft.find((t) => t.id === selectedTask.parent);
        parent.tasks.push(
          Task.BasicTask(
            parent.generateUniqueCode(),
            project,
            generatedId,
            parent.displayIndex +
            parent.taskPackages.length +
            parent.tasks.length,
            generateUniqueName(dft, Task),
            selectedTask.parent,
            selectedTask.parentDisplayIndex,
            formatISO(new Date())
          )
        );
        // Update DisplayIndex of all tasks to match the change in tree hierarchy
        updateDisplayIndex(project.rootTaskPackage.depthFirstTraversal());
        setoptionsMenu(null);
      });
    }
  };

  const handleDeleteTask = () => {
    // Cannot delete task root
    if (selectedTask.id === "0") {
      setoptionsMenu(null);
      return;
    }

    let dft = project.rootTaskPackage.depthFirstTraversal();
    let parent =
      selectedTask.parent === "0"
        ? project.rootTaskPackage
        : dft.find((t) => t.id === selectedTask.parent);
    if (selectedTask instanceof TaskPackage) {
      parent.taskPackages = parent.taskPackages.filter(
        (t) => t.name !== selectedTask.name
      );
    } else if (selectedTask instanceof Task) {
      parent.tasks = parent.tasks.filter((t) => t.name !== selectedTask.name);
    }

    // Update DisplayIndex of all tasks to match the change in tree hierarchy
    updateDisplayIndex(project.rootTaskPackage.depthFirstTraversal());
    setoptionsMenu(null);
  };

  const handleMouseDown = (e) => {
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseMove = useCallback((e) => {
    const newWidth = e.clientX - document.getElementById("taskViewResizableDrawer").offsetLeft;
    if (newWidth > 100 && newWidth < 1000) {
      // setDrawerWidth(newWidth);
    }
  }, []);

  return (
    <div>
      <div
        style={{
          display: "inline-flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h4" gutterBottom component="h2">
          Tasks
        </Typography>
      </div>
      <div style={{ display: "flex", padding: "5px", minHeight: "800px" }}>
        <div style={{ margin: "5px" }}></div>
        <CollapsiblePanel
          width={drawerWidth}
          setWidth={setDrawerWidth}
          open={treeDrawerOpen}
          onToggle={() => setTreeDrawerOpen(!treeDrawerOpen)}
          menuActions={
            <IconButton onClick={handleClickOptionsMenu}>
              <MoreHorizIcon />
            </IconButton>
          }
        >
          <Menu
            anchorEl={optionsMenu}
            open={Boolean(optionsMenu)}
            onClose={() => setoptionsMenu(null)}
          >
            <ListSubheader>Task</ListSubheader>
            <MenuItem onClick={handleAddSummaryTask}>Add Summary Task</MenuItem>
            <MenuItem onClick={handleAddTask}>Add Task</MenuItem>
            <MenuItem onClick={handleDeleteTask}>Delete Task</MenuItem>
            <MenuItem onClick={handlePreBaselineOpen}>Prebaseline Snapshot</MenuItem>
          </Menu>
          <DialogRebaseline open={preBaselineOpen} onCancel={handlePreBaselineClose} task={selectedTask} project={project}/>

          <ProjectTreeView
            fromTask
          />
        </CollapsiblePanel>

        <div style={{ display: "flex", flexDirection: "row", margin: "0px 8px" }}>
          <GetViewPaneForCode selectedTask={selectedTask} />
        </div>
      </div>
    </div>
  );
};

export default observer(TaskView);
